// Action client + useCan hook — single source of truth for UI affordance gating.
//
// Architecture (matches backend):
//   • Backend (action_registry.py)  →  brain (who can do what + preconditions)
//   • This module                   →  thin, batched, request-scoped client
//
// The hook collects every action key requested during a render pass and
// fires ONE POST /api/actions/_check per (range, refresh, login). The
// backend evaluates roles + preconditions and returns a per-action
// Decision; we expose `{allowed, reason}` to the caller.
//
// Final authority lives on the route — this is UX only.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';

const CanContext = createContext({
  decisions: {},
  register: () => {},
  refresh: () => {},
});

export function ActionGateProvider({ children }) {
  // key -> { allowed, reason, failed_preconditions }
  const [decisions, setDecisions] = useState({});
  // Pending registrations collected during the render pass.
  const pendingRef = useRef(new Map()); // key -> ctx
  const flushTimerRef = useRef(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Flush the pending registry into ONE _check call.
  const flush = useCallback(async () => {
    flushTimerRef.current = null;
    const entries = Array.from(pendingRef.current.entries());
    pendingRef.current = new Map();
    if (entries.length === 0) return;

    const keys = entries.map(([k]) => k);
    const ctx = {};
    for (const [k, c] of entries) {
      if (c) ctx[k] = c;
    }
    try {
      const res = await axiosInstance.post('/actions/_check', { keys, ctx });
      const incoming = res.data?.results || {};
      setDecisions((prev) => ({ ...prev, ...incoming }));
    } catch (err) {
      // Fail-closed in the UI; route layer still enforces.
      const denied = {};
      for (const k of keys) {
        denied[k] = { allowed: false, reason: 'check_failed' };
      }
      setDecisions((prev) => ({ ...prev, ...denied }));
    }
  }, []);

  const register = useCallback((actionId, ctx) => {
    if (!actionId) return;
    pendingRef.current.set(actionId, ctx || null);
    if (flushTimerRef.current == null) {
      // Defer to the end of the microtask queue so all hooks in this
      // render pass register before we fire the network call.
      flushTimerRef.current = setTimeout(flush, 0);
    }
  }, [flush]);

  const refresh = useCallback(() => {
    setDecisions({});
    setRefreshTick((t) => t + 1);
  }, []);

  // Reset on refresh tick — hooks will re-register on next render.
  useEffect(() => {
    pendingRef.current = new Map();
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, [refreshTick]);

  const value = useMemo(
    () => ({ decisions, register, refresh }),
    [decisions, register, refresh],
  );

  return <CanContext.Provider value={value}>{children}</CanContext.Provider>;
}

/**
 * Hook: returns `{allowed, reason, loading}` for the given action.
 * Pass `ctx` (e.g. the invoice doc) to evaluate preconditions.
 *
 * Usage:
 *   const { allowed, reason } = useCan('invoice.approve', invoice);
 *   <button disabled={!allowed} title={reason}>Approve</button>
 */
export function useCan(actionId, ctx) {
  const { decisions, register } = useContext(CanContext);
  // Serialize ctx to a stable string for change detection.
  const ctxKey = ctx ? JSON.stringify(ctx) : '';

  useEffect(() => {
    if (actionId) register(actionId, ctx);
  }, [actionId, ctxKey, register]);

  const d = decisions[actionId];
  if (!d) {
    return { allowed: false, reason: null, loading: true };
  }
  return { allowed: !!d.allowed, reason: d.reason, loading: false };
}

/**
 * Wrapper component: renders `children` only when the user is allowed to
 * perform `action`. Falls back to `fallback` (default null).
 *
 * Usage:
 *   <Authorized action="payment.refund" ctx={payment}>
 *     <button>Refund</button>
 *   </Authorized>
 */
export function Authorized({ action, ctx, fallback = null, children }) {
  const { allowed, loading } = useCan(action, ctx);
  if (loading) return null;
  return allowed ? children : fallback;
}

/**
 * Smart button that integrates with the Action Engine.
 *
 *   • `action == null`  → renders as a plain button (no gate check).
 *   • `action` is a registry key → button is disabled while `useCan`
 *     is loading or denied; `title` carries the denial reason for the
 *     tooltip.
 *
 * All other props are forwarded to the underlying <button>.
 */
export function ActionGatedButton({ action, ctx, disabled, title, children, ...rest }) {
  // Skip useCan entirely when action is null so non-gated callers
  // don't incur a backend round-trip.
  const decision = useCan(action || null, ctx);
  // When action is null, useCan returns { allowed: false, loading: true }
  // because the empty key never registers — we treat it as "not gated".
  const isGated = !!action;
  const blocked = isGated && (decision.loading || !decision.allowed);
  const effectiveTitle = blocked && decision.reason ? decision.reason : title;
  return (
    <button
      {...rest}
      title={effectiveTitle}
      disabled={!!disabled || blocked}
    >
      {children}
    </button>
  );
}

export default ActionGateProvider;
